/* eslint-disable consistent-return */
/**
 * Tasks V2 Migration — Coverage Matrix slice (Solution C, §7.4 of the
 * tasks-v2-migration-cockpit-coverage PLAN.md).
 *
 * Why this lives in its own file (and NOT in `tasksV2Migration.js`):
 *   - The cockpit coverage panel is independently shippable and the
 *     three parallel solutions (A, B, C) each touch `tasksV2Migration.js`
 *     would otherwise produce merge conflicts on every single PR.
 *   - The matrix payload is large (one row per `(group, method,
 *     treatment_item)` bucket) and the consumer is a single panel.
 *     Keeping it isolated avoids ballooning the main slice's state.
 *
 * TODO(integrator): register this slice in `src/redux/store.js` under the
 * key `tasksV2CoverageMatrix` so `state.tasksV2CoverageMatrix` is reachable
 * from `useSelector`. The CoverageMatrixPanel reads from that key directly.
 *
 * State shape:
 *   {
 *     byPractice: {
 *       [practiceId]: {
 *         rows: [...],         // matrix rows from BE
 *         totals: {...},       // headline numbers from BE
 *         fetchedAt: ISOString,
 *         loading: bool,
 *         error: string|null,
 *       }
 *     }
 *   }
 */
import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { callAdminApi } from '../../utils/apiCall';

const BASE = '/api/v1/management/tasks-migration';

const emptyPracticeState = () => ({
    rows: [],
    totals: null,
    fetchedAt: null,
    loading: false,
    error: null,
});

const initialState = {
    byPractice: {},
};

const slice = createSlice({
    name: 'tasksV2CoverageMatrix',
    initialState,
    reducers: {
        setLoading(state, action) {
            const { practiceId, value } = action.payload;
            if (!state.byPractice[practiceId]) {
                state.byPractice[practiceId] = emptyPracticeState();
            }
            state.byPractice[practiceId].loading = value;
            if (value) state.byPractice[practiceId].error = null;
        },
        setError(state, action) {
            const { practiceId, error } = action.payload;
            if (!state.byPractice[practiceId]) {
                state.byPractice[practiceId] = emptyPracticeState();
            }
            state.byPractice[practiceId].error = error;
            state.byPractice[practiceId].loading = false;
        },
        setMatrix(state, action) {
            const { practiceId, payload } = action.payload;
            state.byPractice[practiceId] = {
                rows: payload?.rows || [],
                totals: payload?.totals || null,
                fetchedAt: payload?.fetched_at || null,
                loading: false,
                error: null,
            };
        },
        clear(state, action) {
            const { practiceId } = action.payload || {};
            if (practiceId) {
                delete state.byPractice[practiceId];
            } else {
                state.byPractice = {};
            }
        },
    },
});

export default slice.reducer;
export const { setLoading, setError, setMatrix, clear } = slice.actions;

/**
 * fetchCoverageMatrix(practiceId, { fresh })
 *   GET /api/v1/management/tasks-migration/practices/{id}/coverage-matrix/[?fresh=1]
 *
 * Honours the BE 60s cache by default. Pass `{ fresh: true }` to bust
 * the cache (e.g. after the operator edits an override and wants the
 * matrix to reflect the new resolution).
 */
export function fetchCoverageMatrix(practiceId, { fresh = false } = {}) {
    return async () => {
        if (!practiceId) return null;
        try {
            dispatch(slice.actions.setLoading({ practiceId, value: true }));
            const url = `${BASE}/practices/${practiceId}/coverage-matrix/${fresh ? '?fresh=1' : ''}`;
            // Same generous timeout as diagnostics — the BE walks the
            // resolver per (group, treatment) pair, which can be slow on
            // large practices on a cold cache.
            const response = await callAdminApi(url, 'GET', null, { timeout: 30000 });
            if (response.status === 200) {
                dispatch(slice.actions.setMatrix({
                    practiceId,
                    payload: response.data,
                }));
            } else if (response.status === 0) {
                dispatch(slice.actions.setError({
                    practiceId,
                    error: 'Network timeout fetching coverage matrix.',
                }));
            } else {
                const detail = response.data?.detail
                    || `Failed to fetch coverage matrix (HTTP ${response.status})`;
                dispatch(slice.actions.setError({ practiceId, error: detail }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setError({ practiceId, error: message }));
            return { ok: false, status: 0, error };
        }
    };
}
